import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toPng } from "html-to-image";
import {
    ArrowLeft,
    ArrowRight,
    Wrench,
    DollarSign,
    History,
    AlertTriangle,
    HelpCircle,
    Check,
    Download,
    Share2,
    Loader2,
    Sparkles,
    ShieldCheck,
    Car,
    ExternalLink,
} from "lucide-react";
import Reveal from "../components/Reveal";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { joinWaitlist } from "../lib/api";

const LOGO_URL = "/images/logo.png";
const TYPICAL_CAR_PAYMENT = 450;

/* ---------------- Verdict logic ---------------- */
function getVerdict({
    repairQuote,
    replacementCost,
    last12moRepairs,
    nextProblem,
    canAfford,
}) {
    const paymentMonths = repairQuote / TYPICAL_CAR_PAYMENT;
    const totalSunk = repairQuote + last12moRepairs;
    const sunkRatio =
        replacementCost > 0 ? totalSunk / replacementCost : 0;

    let riskScore = 0;
    if (nextProblem === "recurring") riskScore += 2;
    if (nextProblem === "more_coming") riskScore += 3;
    if (nextProblem === "first") riskScore -= 1;
    if (sunkRatio >= 0.5) riskScore += 2;
    else if (sunkRatio >= 0.3) riskScore += 1;
    if (paymentMonths <= 4) riskScore -= 1;
    if (paymentMonths >= 12) riskScore += 1;

    let verdict;
    if (riskScore >= 4) verdict = "replace";
    else if (riskScore <= 0) verdict = "repair";
    else verdict = "close";

    let constrained = false;
    if (canAfford === "not_realistic" && verdict === "replace") {
        verdict = "close";
        constrained = true;
    }

    // Apples-to-apples check: detect when the "replacement" looks like an upgrade,
    // not a comparable car. Heuristic: repair is a tiny fraction of replacement (<~7%).
    // Catches e.g. $2,400 repair vs $45,000 truck (0.053) while leaving
    // $2,400 vs $12,000 comparable car (0.20) alone.
    const repairToReplaceRatio =
        replacementCost > 0 ? repairQuote / replacementCost : 1;
    const likelyUpgrade = repairToReplaceRatio < 0.07;

    return {
        verdict,
        paymentMonths,
        totalSunk,
        sunkRatio,
        riskScore,
        constrained,
        likelyUpgrade,
    };
}

const VERDICT_COPY = {
    repair: {
        label: "Repair it.",
        sub: "The math says this car still has runway. Fix it and keep driving.",
        tone: "emerald",
    },
    replace: {
        label: "Time to replace.",
        sub: "You're throwing good money at a car that's signaling the end. Plan the next move.",
        tone: "amber",
    },
    close: {
        label: "It's close — your call.",
        sub: "The numbers don't strongly favor either side. Weigh how much you trust this car going forward.",
        tone: "navy",
    },
};

/* ---------------- UI helpers ---------------- */
function StepHeader({ step, total, title, sub }) {
    return (
        <div>
            <div className="flex items-center gap-3">
                <div className="text-xs font-mono uppercase tracking-[0.22em] text-[#10B981] font-bold">
                    Step {step} of {total}
                </div>
                <div className="h-px flex-1 bg-slate-200" />
            </div>
            <h2 className="mt-4 text-3xl sm:text-4xl font-black tracking-tight text-[#0C447C]">
                {title}
            </h2>
            {sub && (
                <p className="mt-2 text-slate-600 leading-relaxed">
                    {sub}
                </p>
            )}
        </div>
    );
}

function CurrencyInput({ value, onChange, placeholder, testId, autoFocus }) {
    return (
        <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 font-bold text-lg">
                $
            </span>
            <Input
                type="number"
                inputMode="decimal"
                min="0"
                step="1"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                data-testid={testId}
                autoFocus={autoFocus}
                className="h-14 pl-9 pr-4 text-xl font-bold bg-white border-slate-200 text-[#0C447C] placeholder:text-slate-300 rounded-xl focus-visible:ring-[#10B981] focus-visible:ring-offset-0 focus-visible:border-[#10B981]"
            />
        </div>
    );
}

function ChoiceCard({ active, onClick, icon: Icon, title, desc, testId }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testId}
            data-active={active}
            className={`text-left w-full p-5 rounded-2xl border-2 transition-all duration-300 group ${
                active
                    ? "border-[#10B981] bg-emerald-50/70 shadow-[0_8px_24px_-12px_rgba(16,185,129,0.45)]"
                    : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50"
            }`}
        >
            <div className="flex items-start gap-4">
                <div
                    className={`shrink-0 w-11 h-11 rounded-xl grid place-items-center transition-colors ${
                        active
                            ? "bg-[#10B981] text-white"
                            : "bg-slate-100 text-slate-500 group-hover:bg-slate-200"
                    }`}
                >
                    <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1">
                    <div className="font-bold text-[#0C447C]">{title}</div>
                    {desc && (
                        <div className="text-sm text-slate-600 mt-1 leading-relaxed">
                            {desc}
                        </div>
                    )}
                </div>
                <div
                    className={`shrink-0 w-5 h-5 rounded-full border-2 grid place-items-center transition-colors ${
                        active
                            ? "border-[#10B981] bg-[#10B981]"
                            : "border-slate-300"
                    }`}
                >
                    {active && (
                        <Check
                            className="w-3 h-3 text-white"
                            strokeWidth={3}
                        />
                    )}
                </div>
            </div>
        </button>
    );
}

/* ---------------- Page ---------------- */
export default function FixOrReplace() {
    const [step, setStep] = useState(1);
    const totalSteps = 5;
    const [data, setData] = useState({
        repairQuote: "",
        replacementCost: "",
        last12moRepairs: "",
        nextProblem: "",
        canAfford: "",
    });
    const [result, setResult] = useState(null);
    const resultCardRef = useRef(null);

    const update = (k, v) => setData((d) => ({ ...d, [k]: v }));

    const stepValid = () => {
        if (step === 1) return Number(data.repairQuote) > 0;
        if (step === 2) return Number(data.replacementCost) > 0;
        if (step === 3) return data.last12moRepairs !== "" && Number(data.last12moRepairs) >= 0;
        if (step === 4) return !!data.nextProblem;
        if (step === 5) return !!data.canAfford;
        return false;
    };

    const next = () => {
        if (!stepValid()) return;
        if (step < totalSteps) {
            setStep(step + 1);
        } else {
            const r = getVerdict({
                repairQuote: Number(data.repairQuote),
                replacementCost: Number(data.replacementCost),
                last12moRepairs: Number(data.last12moRepairs),
                nextProblem: data.nextProblem,
                canAfford: data.canAfford,
            });
            setResult(r);
            setTimeout(
                () =>
                    document
                        .getElementById("result-card")
                        ?.scrollIntoView({ behavior: "smooth" }),
                100
            );
        }
    };

    const back = () => {
        if (result) {
            setResult(null);
            return;
        }
        if (step > 1) setStep(step - 1);
    };

    const reset = () => {
        setResult(null);
        setStep(1);
        setData({
            repairQuote: "",
            replacementCost: "",
            last12moRepairs: "",
            nextProblem: "",
            canAfford: "",
        });
    };

    return (
        <main
            data-testid="fix-or-replace-page"
            className="relative bg-white"
        >
            {/* HERO */}
            <section className="relative pt-44 sm:pt-48 pb-12 overflow-hidden mx-mint-gradient">
                <div className="absolute inset-0 mx-grid-bg opacity-50 pointer-events-none" />
                <div className="absolute -top-20 right-0 w-[420px] h-[420px] rounded-full bg-[#10B981]/10 blur-3xl pointer-events-none" />
                <div className="relative max-w-3xl mx-auto px-6 lg:px-8">
                    <Reveal>
                        <Link
                            to="/"
                            data-testid="back-home"
                            className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-[#0C447C] transition-colors"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            Back to home
                        </Link>
                    </Reveal>
                    <Reveal delay={0.05}>
                        <div className="mt-8 inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#10B981] font-bold">
                            <span className="w-6 h-px bg-[#10B981]" />
                            Fix-or-Replace Calculator
                        </div>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.05] text-[#0C447C]">
                            Should you fix it,{" "}
                            <span className="text-[#10B981]">
                                or move on?
                            </span>
                        </h1>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <p className="mt-5 text-lg text-slate-600 leading-relaxed">
                            Answer 5 quick questions and we&apos;ll tell you
                            whether repairing or replacing makes more
                            financial sense. No sign-up, no upsell, no
                            judgment.
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* FORM CARD */}
            <section className="relative pb-20 -mt-6">
                <div className="max-w-3xl mx-auto px-6 lg:px-8">
                    <Reveal>
                        <div className="relative bg-white rounded-3xl border border-slate-200 shadow-[0_24px_60px_-20px_rgba(7,15,29,0.12)] overflow-hidden">
                            {/* Progress bar */}
                            <div className="h-1.5 bg-slate-100">
                                <motion.div
                                    initial={false}
                                    animate={{
                                        width: result
                                            ? "100%"
                                            : `${(step / totalSteps) * 100}%`,
                                    }}
                                    transition={{
                                        duration: 0.5,
                                        ease: [0.16, 1, 0.3, 1],
                                    }}
                                    className="h-full bg-gradient-to-r from-[#10B981] to-[#34D399]"
                                />
                            </div>

                            <div className="p-6 sm:p-10">
                                {!result && (
                                    <AnimatePresence mode="wait">
                                        <motion.div
                                            key={step}
                                            initial={{ opacity: 0, x: 20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            exit={{ opacity: 0, x: -20 }}
                                            transition={{ duration: 0.3 }}
                                        >
                                            {step === 1 && (
                                                <div>
                                                    <StepHeader
                                                        step={1}
                                                        total={totalSteps}
                                                        title="How much is the repair estimate?"
                                                        sub="Enter the total amount your mechanic quoted for the repair you're considering."
                                                    />
                                                    <div className="mt-8">
                                                        <CurrencyInput
                                                            value={data.repairQuote}
                                                            onChange={(v) =>
                                                                update(
                                                                    "repairQuote",
                                                                    v
                                                                )
                                                            }
                                                            placeholder="0"
                                                            testId="input-repair-quote"
                                                            autoFocus
                                                        />
                                                    </div>
                                                </div>
                                            )}

                                            {step === 2 && (
                                                <div>
                                                    <StepHeader
                                                        step={2}
                                                        total={totalSteps}
                                                        title="How much would you spend on a replacement vehicle?"
                                                        sub="Enter the price of a comparable car — similar size, age, and condition to what you have now. Not the upgrade you'd love to have."
                                                    />
                                                    <div className="mt-8 space-y-3">
                                                        <CurrencyInput
                                                            value={
                                                                data.replacementCost
                                                            }
                                                            onChange={(v) =>
                                                                update(
                                                                    "replacementCost",
                                                                    v
                                                                )
                                                            }
                                                            placeholder="0"
                                                            testId="input-replacement-cost"
                                                            autoFocus
                                                        />
                                                        <div className="flex justify-end text-sm text-slate-500">
                                                            <a
                                                                href="https://www.kbb.com"
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                data-testid="kbb-link"
                                                                className="inline-flex items-center gap-1 text-[#10B981] font-bold hover:text-[#059669]"
                                                            >
                                                                Look up your current car&apos;s value on KBB
                                                                <ExternalLink className="w-3.5 h-3.5" />
                                                            </a>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {step === 3 && (
                                                <div>
                                                    <StepHeader
                                                        step={3}
                                                        total={totalSteps}
                                                        title="How much have you spent on repairs in the past 12 months?"
                                                        sub="Exclude the current estimate. An approximate amount is fine."
                                                    />
                                                    <div className="mt-8">
                                                        <CurrencyInput
                                                            value={
                                                                data.last12moRepairs
                                                            }
                                                            onChange={(v) =>
                                                                update(
                                                                    "last12moRepairs",
                                                                    v
                                                                )
                                                            }
                                                            placeholder="0"
                                                            testId="input-last12mo"
                                                            autoFocus
                                                        />
                                                        <p className="mt-3 text-sm text-slate-500">
                                                            Put 0 if nothing
                                                            major.
                                                        </p>
                                                    </div>
                                                </div>
                                            )}

                                            {step === 4 && (
                                                <div>
                                                    <StepHeader
                                                        step={4}
                                                        total={totalSteps}
                                                        title="How's this car been treating you lately?"
                                                        sub="Choose the option that best fits."
                                                    />
                                                    <div className="mt-8 space-y-3">
                                                        <ChoiceCard
                                                            active={
                                                                data.nextProblem ===
                                                                "first"
                                                            }
                                                            onClick={() =>
                                                                update(
                                                                    "nextProblem",
                                                                    "first"
                                                                )
                                                            }
                                                            icon={ShieldCheck}
                                                            title="First major repair"
                                                            desc="Car's been solid — this is the first real problem."
                                                            testId="choice-first"
                                                        />
                                                        <ChoiceCard
                                                            active={
                                                                data.nextProblem ===
                                                                "recurring"
                                                            }
                                                            onClick={() =>
                                                                update(
                                                                    "nextProblem",
                                                                    "recurring"
                                                                )
                                                            }
                                                            icon={History}
                                                            title="Frequent repairs"
                                                            desc="It's been a steady drip of repairs lately."
                                                            testId="choice-recurring"
                                                        />
                                                        <ChoiceCard
                                                            active={
                                                                data.nextProblem ===
                                                                "more_coming"
                                                            }
                                                            onClick={() =>
                                                                update(
                                                                    "nextProblem",
                                                                    "more_coming"
                                                                )
                                                            }
                                                            icon={AlertTriangle}
                                                            title="More expected"
                                                            desc="Your mechanic flagged other issues coming."
                                                            testId="choice-more"
                                                        />
                                                    </div>
                                                </div>
                                            )}

                                            {step === 5 && (
                                                <div>
                                                    <StepHeader
                                                        step={5}
                                                        total={totalSteps}
                                                        title="Could you realistically afford a replacement right now?"
                                                        sub="Realistic check — replacing only helps if it's actually on the table."
                                                    />
                                                    <div className="mt-8 space-y-3">
                                                        <ChoiceCard
                                                            active={
                                                                data.canAfford ===
                                                                "yes"
                                                            }
                                                            onClick={() =>
                                                                update(
                                                                    "canAfford",
                                                                    "yes"
                                                                )
                                                            }
                                                            icon={Check}
                                                            title="Yes"
                                                            desc="Down payment, financing, or cash — it's an option."
                                                            testId="choice-yes"
                                                        />
                                                        <ChoiceCard
                                                            active={
                                                                data.canAfford ===
                                                                "not_realistic"
                                                            }
                                                            onClick={() =>
                                                                update(
                                                                    "canAfford",
                                                                    "not_realistic"
                                                                )
                                                            }
                                                            icon={HelpCircle}
                                                            title="Not realistic right now"
                                                            desc="Replacing isn't on the table — we need to make this work."
                                                            testId="choice-not-realistic"
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </motion.div>
                                    </AnimatePresence>
                                )}

                                {/* Result card */}
                                {result && (
                                    <ResultBlock
                                        result={result}
                                        data={data}
                                        ref={resultCardRef}
                                        onReset={reset}
                                    />
                                )}
                            </div>

                            {/* Footer / nav */}
                            {!result && (
                                <div className="border-t border-slate-200 bg-slate-50/60 px-6 sm:px-10 py-5 flex items-center justify-between">
                                    <button
                                        type="button"
                                        onClick={back}
                                        disabled={step === 1}
                                        data-testid="step-back"
                                        className="inline-flex items-center gap-2 text-sm font-bold text-slate-500 hover:text-[#0C447C] disabled:opacity-30 disabled:hover:text-slate-500 transition-colors"
                                    >
                                        <ArrowLeft className="w-4 h-4" />
                                        Back
                                    </button>
                                    <Button
                                        type="button"
                                        onClick={next}
                                        disabled={!stepValid()}
                                        data-testid="step-next"
                                        className="bg-[#10B981] hover:bg-[#059669] text-white font-bold rounded-full px-6 h-11 shadow-[0_8px_20px_-6px_rgba(16,185,129,0.5)] disabled:opacity-40 disabled:hover:bg-[#10B981] group"
                                    >
                                        <span className="inline-flex items-center gap-2">
                                            {step === totalSteps
                                                ? "See my verdict"
                                                : "Continue"}
                                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                        </span>
                                    </Button>
                                </div>
                            )}
                        </div>
                    </Reveal>

                    {/* Disclaimer */}
                    <p className="mt-6 text-center text-xs text-slate-500 leading-relaxed max-w-xl mx-auto">
                        We don&apos;t price-check repairs or value cars for you —
                        the math uses only your inputs. For replacement value,
                        check{" "}
                        <a
                            href="https://www.kbb.com/car-values/"
                            target="_blank"
                            rel="noreferrer"
                            className="text-[#10B981] font-bold"
                        >
                            Kelley Blue Book
                        </a>
                        .
                    </p>
                </div>
            </section>
        </main>
    );
}

/* ---------------- Result Block + Card + Email ---------------- */
const ResultBlock = function ResultBlock({ result, data, onReset }) {
    const copy = VERDICT_COPY[result.verdict];
    const cardRef = useRef(null);
    const [downloading, setDownloading] = useState(false);

    // Email capture
    const [email, setEmail] = useState("");
    const [zip, setZip] = useState("");
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);

    const repairAmount = Number(data.repairQuote);
    const replacementAmount = Number(data.replacementCost);
    const last12mo = Number(data.last12moRepairs);
    const months = result.paymentMonths;

    const downloadImage = async () => {
        if (!cardRef.current) return;
        setDownloading(true);
        try {
            const png = await toPng(cardRef.current, {
                pixelRatio: 2,
                backgroundColor: "#ffffff",
                cacheBust: true,
            });
            const a = document.createElement("a");
            a.href = png;
            a.download = `mechxpress-${result.verdict}.png`;
            a.click();
            toast.success("Image downloaded");
        } catch (e) {
            toast.error("Couldn't generate image. Try again.");
        } finally {
            setDownloading(false);
        }
    };

    const share = async () => {
        if (!cardRef.current) return;
        try {
            const png = await toPng(cardRef.current, {
                pixelRatio: 2,
                backgroundColor: "#ffffff",
                cacheBust: true,
            });
            const blob = await (await fetch(png)).blob();
            const file = new File([blob], `mechxpress-${result.verdict}.png`, {
                type: "image/png",
            });
            const shareText =
                result.verdict === "repair"
                    ? "MechXpress says repair it."
                    : result.verdict === "replace"
                      ? "MechXpress says it's time to replace."
                      : "MechXpress: this one's a close call.";
            if (navigator.canShare && navigator.canShare({ files: [file] })) {
                await navigator.share({
                    files: [file],
                    title: "Fix or Replace?",
                    text: shareText,
                });
            } else {
                await navigator.clipboard.writeText(
                    `${shareText} ${window.location.origin}/fix-or-replace`
                );
                toast.success("Link copied");
            }
        } catch (e) {
            // user cancelled or browser blocked — silent
        }
    };

    const saveEmail = async (e) => {
        e.preventDefault();
        try {
            setSaving(true);
            await joinWaitlist({
                email,
                role: "customer",
                zip_code: zip || null,
                source: "calculator",
            });
            setSaved(true);
            toast.success("Saved. We'll be in touch.");
        } catch (err) {
            toast.error(
                err?.response?.data?.detail || "Couldn't save right now."
            );
        } finally {
            setSaving(false);
        }
    };

    const toneStyles = {
        emerald: {
            bg: "from-emerald-50 via-white to-emerald-50",
            badge: "bg-emerald-100 text-[#059669]",
            accent: "text-[#10B981]",
            ring: "border-emerald-200",
        },
        amber: {
            bg: "from-amber-50 via-white to-amber-50",
            badge: "bg-amber-100 text-amber-700",
            accent: "text-amber-600",
            ring: "border-amber-200",
        },
        navy: {
            bg: "from-slate-50 via-white to-slate-50",
            badge: "bg-slate-100 text-[#0C447C]",
            accent: "text-[#0C447C]",
            ring: "border-slate-200",
        },
    };
    const t = toneStyles[copy.tone];

    const highSpend = last12mo > 0 && last12mo + repairAmount >= replacementAmount * 0.3;

    const fmt = (n) => `$${Number(n).toLocaleString()}`;

    return (
        <div id="result-card">
            {/* Apples-to-apples honesty callout — shown above the verdict card */}
            {result.likelyUpgrade && (
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                    data-testid="upgrade-note"
                    className="mb-5 flex items-start gap-3 rounded-2xl border border-[#0C447C]/20 bg-[#0C447C]/[0.04] px-5 py-4"
                >
                    <HelpCircle className="w-5 h-5 mt-0.5 text-[#0C447C] shrink-0" />
                    <div className="text-sm text-slate-700 leading-relaxed">
                        <strong className="text-[#0C447C]">Heads up:</strong>{" "}
                        a {fmt(repairAmount)} repair against a ~
                        {fmt(replacementAmount)} vehicle is comparing your
                        car to an upgrade, not a like-for-like replacement.
                        The math below assumes you&apos;d buy something
                        comparable to what you have. If you genuinely want to
                        upgrade, that&apos;s a separate decision — the repair
                        will almost always &quot;win&quot; on cost alone,
                        because a newer, nicer car costs more by definition.
                    </div>
                </motion.div>
            )}

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                ref={cardRef}
                data-testid="result-card"
                className={`relative rounded-2xl border ${t.ring} bg-gradient-to-br ${t.bg} p-8 sm:p-10 overflow-hidden`}
            >
                {/* watermark logo */}
                <div className="flex items-center justify-between mb-6">
                    <img
                        src={LOGO_URL}
                        alt="MechXpress"
                        crossOrigin="anonymous"
                        className="h-10 w-auto rounded-lg"
                    />
                    <div
                        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider ${t.badge}`}
                    >
                        <Sparkles className="w-3 h-3" />
                        Verdict
                    </div>
                </div>

                <div className="text-xs uppercase tracking-[0.22em] font-bold text-slate-500">
                    Fix-or-Replace
                </div>
                <h3
                    className={`mt-2 text-4xl sm:text-5xl font-black tracking-tight ${t.accent} leading-tight`}
                    data-testid="verdict-label"
                >
                    {copy.label}
                </h3>
                <p className="mt-3 text-slate-700 leading-relaxed max-w-xl">
                    {copy.sub}
                </p>

                {result.constrained && (
                    <div className="mt-5 inline-flex items-start gap-2 rounded-xl bg-white border border-slate-200 px-4 py-3 max-w-xl">
                        <HelpCircle className="w-4 h-4 mt-0.5 text-[#0C447C] shrink-0" />
                        <span className="text-sm text-slate-700 leading-relaxed">
                            We don&apos;t push &quot;replace&quot; when it&apos;s not realistic.
                            Focus on this fix and what to watch next.
                        </span>
                    </div>
                )}

                {/* Stats grid */}
                <div className="mt-8 grid sm:grid-cols-3 gap-4">
                    <Stat
                        label="This repair"
                        value={`$${repairAmount.toLocaleString()}`}
                        sub={`= ${months.toFixed(1)} months of a typical $${TYPICAL_CAR_PAYMENT} car payment`}
                    />
                    <Stat
                        label="Spent last 12 months"
                        value={`$${last12mo.toLocaleString()}`}
                        sub={`Total recent: $${result.totalSunk.toLocaleString()}`}
                    />
                    <Stat
                        label="vs. a replacement"
                        value={`${Math.round(result.sunkRatio * 100)}%`}
                        sub={`of a $${replacementAmount.toLocaleString()} car`}
                    />
                </div>

                {highSpend && (
                    <div className="mt-6 flex items-start gap-3 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3">
                        <AlertTriangle className="w-5 h-5 mt-0.5 text-amber-600 shrink-0" />
                        <div className="text-sm text-amber-900 leading-relaxed">
                            <strong>Heads up:</strong> You&apos;ve already put
                            ${(result.totalSunk).toLocaleString()} into this
                            car recently — that&apos;s{" "}
                            {Math.round(result.sunkRatio * 100)}% of a
                            replacement. Worth tracking how often it asks for
                            more.
                        </div>
                    </div>
                )}

                <div className="mt-8 pt-6 border-t border-slate-200 text-xs text-slate-500">
                    Generated by MechXpress · Fix-or-Replace Calculator ·
                    mechxpress.com
                </div>
            </motion.div>

            {/* Actions */}
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <Button
                    onClick={downloadImage}
                    disabled={downloading}
                    data-testid="download-image"
                    className="flex-1 h-12 bg-[#0C447C] hover:bg-[#0a3a6b] text-white font-bold rounded-full shadow-[0_8px_20px_-6px_rgba(12,68,124,0.5)]"
                >
                    {downloading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                        <span className="inline-flex items-center gap-2">
                            <Download className="w-4 h-4" />
                            Download image
                        </span>
                    )}
                </Button>
                <Button
                    onClick={share}
                    data-testid="share-result"
                    variant="outline"
                    className="flex-1 h-12 bg-white border-slate-300 text-[#0C447C] font-bold rounded-full hover:bg-slate-50"
                >
                    <span className="inline-flex items-center gap-2">
                        <Share2 className="w-4 h-4" />
                        Share
                    </span>
                </Button>
                <Button
                    onClick={onReset}
                    data-testid="reset"
                    variant="ghost"
                    className="h-12 text-slate-500 hover:text-[#0C447C] font-bold rounded-full"
                >
                    Run again
                </Button>
            </div>

            {/* Email capture */}
            <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm">
                {saved ? (
                    <div
                        data-testid="calc-save-success"
                        className="text-center"
                    >
                        <div className="mx-auto w-12 h-12 rounded-full bg-emerald-50 grid place-items-center mb-3">
                            <Check className="w-6 h-6 text-[#10B981]" />
                        </div>
                        <h4 className="text-lg font-bold text-[#0C447C]">
                            Saved.
                        </h4>
                        <p className="text-sm text-slate-600 mt-1">
                            We&apos;ll email you a heads-up on what to watch next.
                        </p>
                    </div>
                ) : (
                    <form
                        onSubmit={saveEmail}
                        data-testid="calc-save-form"
                    >
                        <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-100 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-[#059669]">
                            <Wrench className="w-3 h-3" />
                            Optional
                        </div>
                        <h4 className="mt-3 text-xl font-bold text-[#0C447C]">
                            Save this + get a heads-up on what to watch next.
                        </h4>
                        <p className="mt-1 text-sm text-slate-600">
                            We&apos;ll let you know when MechXpress goes live in
                            your area.
                        </p>
                        <div className="mt-5 grid sm:grid-cols-[1fr_140px_auto] gap-3">
                            <Input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@email.com"
                                data-testid="calc-email"
                                className="h-12 bg-white border-slate-200 text-[#0C447C] placeholder:text-slate-400 rounded-xl focus-visible:ring-[#10B981] focus-visible:ring-offset-0"
                            />
                            <Input
                                value={zip}
                                onChange={(e) => setZip(e.target.value)}
                                placeholder="ZIP"
                                data-testid="calc-zip"
                                className="h-12 bg-white border-slate-200 text-[#0C447C] placeholder:text-slate-400 rounded-xl focus-visible:ring-[#10B981] focus-visible:ring-offset-0"
                            />
                            <Button
                                type="submit"
                                disabled={saving}
                                data-testid="calc-save-submit"
                                className="h-12 px-6 bg-[#10B981] hover:bg-[#059669] text-white font-bold rounded-full"
                            >
                                {saving ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    "Save"
                                )}
                            </Button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

function Stat({ label, value, sub }) {
    return (
        <div className="rounded-xl bg-white border border-slate-200 p-4">
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                {label}
            </div>
            <div className="mt-1 text-2xl font-black tracking-tight text-[#0C447C]">
                {value}
            </div>
            <div className="mt-1 text-xs text-slate-500 leading-snug">
                {sub}
            </div>
        </div>
    );
}
